import { useEffect, useState, useCallback } from "react";
import api, { errMsg, fmtQty, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, PackageCheck, Box, Truck } from "lucide-react";

export default function Shipments() {
  const { t } = useI18n();
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ customer_id: "", warehouse: "", notes: "", lines: [{ item_number: "", qty: 1 }] });
  const [picks, setPicks] = useState({});
  const [packages, setPackages] = useState(1);

  const load = useCallback(() => api.get("/shipments").then((r) => setRows(r.data)), []);
  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data));
    api.get("/address-book", { params: { type: "customer" } }).then((r) => setCustomers(r.data));
    api.get("/warehouses").then((r) => setWarehouses(r.data));
    api.get("/locations").then((r) => setLocations(r.data));
  }, [load]);

  const save = async () => {
    try {
      const payload = { ...form, lines: form.lines.filter((l) => l.item_number).map((l) => ({ item_number: l.item_number, qty: Number(l.qty) })) };
      await api.post("/shipments", payload);
      toast.success(t("common.created"));
      setOpen(false);
      setForm({ customer_id: "", warehouse: "", notes: "", lines: [{ item_number: "", qty: 1 }] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openDetail = (so) => {
    setDetail(so);
    const init = {};
    so.lines.forEach((l) => { init[l.item_number] = { location: "", qty: l.qty }; });
    (so.pick_lines || []).forEach((p) => { init[p.item_number] = { location: p.location, qty: p.qty }; });
    setPicks(init);
    setPackages(so.packages || 1);
  };

  const doAction = async (path, body, msg) => {
    try {
      const { data } = await api.post(`/shipments/${detail.id}/${path}`, body);
      toast.success(msg);
      setDetail(data);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const confirmPick = () => doAction("pick", {
    lines: detail.lines.map((l) => ({ item_number: l.item_number,
      location: picks[l.item_number]?.location || "", qty: Number(picks[l.item_number]?.qty || 0) })),
  }, t("so.picked"));

  const whLocations = detail ? locations.filter((l) => l.warehouse === detail.warehouse) : [];

  return (
    <div data-testid="shipments-page">
      <PageHeader title={t("so.title")} subtitle={t("so.subtitle")}>
        {can("admin", "buyer", "warehouse") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="rounded-none text-xs font-bold" data-testid="shipment-new-button">
                <Plus size={13} className="mr-1" /> {t("so.new")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-2xl">
              <DialogHeader>
                <DialogTitle className="uppercase font-black">{t("so.new")}</DialogTitle>
                <DialogDescription className="text-xs">{t("so.subtitle")}</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("so.customer")}</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="shipment-form-customer"><SelectValue /></SelectTrigger>
                    <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.warehouse")}</Label>
                  <Select value={form.warehouse} onValueChange={(v) => setForm({ ...form, warehouse: v })}>
                    <SelectTrigger className="rounded-none h-8 text-xs" data-testid="shipment-form-warehouse"><SelectValue /></SelectTrigger>
                    <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.code}>{w.code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.lines")}</Label>
                {form.lines.map((l, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Select value={l.item_number} onValueChange={(v) => {
                      const lines = [...form.lines]; lines[i] = { ...l, item_number: v }; setForm({ ...form, lines });
                    }}>
                      <SelectTrigger className="rounded-none h-8 text-xs flex-1" data-testid={`shipment-line-item-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{items.map((it) => <SelectItem key={it.id} value={it.item_number}>{it.item_number} — {it.description}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" value={l.qty} onChange={(e) => {
                      const lines = [...form.lines]; lines[i] = { ...l, qty: e.target.value }; setForm({ ...form, lines });
                    }} className="rounded-none h-8 text-xs w-20" data-testid={`shipment-line-qty-${i}`} />
                    <button onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}
                      className="text-muted-foreground hover:text-destructive"><Trash2 size={13} /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="shipment-add-line-button"
                  onClick={() => setForm({ ...form, lines: [...form.lines, { item_number: "", qty: 1 }] })}>
                  <Plus size={12} className="mr-1" /> {t("common.addLine")}
                </Button>
              </div>
              <Button onClick={save} disabled={!form.customer_id || !form.warehouse || !form.lines.some((l) => l.item_number)}
                className="rounded-none font-bold" data-testid="shipment-save-button">{t("common.create")}</Button>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      {rows.length === 0 ? <Empty text={t("common.noData")} /> : (
        <div className="tbl-wrap" data-testid="shipments-table">
          <table className="tbl">
            <thead><tr>
              <th>{t("common.number")}</th><th>{t("common.status")}</th><th>{t("so.customer")}</th>
              <th>{t("common.warehouse")}</th><th className="num">{t("common.lines")}</th>
              <th className="num">{t("so.packages")}</th><th>{t("common.date")}</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="cursor-pointer" onClick={() => openDetail(r)}>
                  <td className="font-bold">{r.number}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td>{r.customer_name}</td>
                  <td>{r.warehouse}</td>
                  <td className="num">{r.lines.length}</td>
                  <td className="num">{r.packages || "—"}</td>
                  <td className="text-muted-foreground">{fmtDate(r.created_at)}</td>
                  <td><span className="text-[10px] font-bold text-primary" data-testid={`shipment-view-${r.number}`}>{t("common.view")}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="rounded-none sm:max-w-2xl">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="uppercase font-black flex items-center gap-3">
                  {detail.number} <StatusBadge status={detail.status} />
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {detail.customer_name} · {detail.warehouse}
                </DialogDescription>
              </DialogHeader>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead><tr>
                    <th>{t("common.item")}</th><th className="num">{t("common.qty")}</th>
                    {detail.status === "open" && can("admin", "warehouse") && <th>{t("so.pickFrom")}</th>}
                    {detail.status !== "open" && <th>{t("so.pickFrom")}</th>}
                  </tr></thead>
                  <tbody>
                    {detail.lines.map((l) => (
                      <tr key={l.item_number}>
                        <td className="font-bold">{l.item_number} <span className="font-normal text-muted-foreground">{l.description}</span></td>
                        <td className="num font-bold">{fmtQty(l.qty)}</td>
                        {detail.status === "open" && can("admin", "warehouse") ? (
                          <td>
                            <Select value={picks[l.item_number]?.location ?? ""} onValueChange={(v) =>
                              setPicks({ ...picks, [l.item_number]: { ...picks[l.item_number], location: v === "none" ? "" : v } })}>
                              <SelectTrigger className="rounded-none h-7 text-xs w-44" data-testid={`pick-location-${l.item_number}`}><SelectValue placeholder={t("loc.none")} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{t("loc.none")}</SelectItem>
                                {whLocations.map((loc) => <SelectItem key={loc.id} value={loc.code}>{loc.code}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                        ) : detail.status !== "open" ? (
                          <td>{(detail.pick_lines || []).find((p) => p.item_number === l.item_number)?.location || "—"}</td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {can("admin", "warehouse") && (
                <div className="flex gap-2 items-end flex-wrap">
                  {detail.status === "open" && (
                    <Button size="sm" onClick={confirmPick} className="rounded-none text-xs font-bold" data-testid="shipment-pick-button">
                      <PackageCheck size={13} className="mr-1" /> {t("so.pick")}
                    </Button>
                  )}
                  {detail.status === "picked" && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-[10px] font-bold uppercase tracking-widest">{t("so.packages")}</Label>
                        <Input type="number" min="1" value={packages} onChange={(e) => setPackages(e.target.value)}
                          className="rounded-none h-8 text-xs w-24" data-testid="shipment-packages-input" />
                      </div>
                      <Button size="sm" onClick={() => doAction("pack", { packages: Number(packages) }, t("so.packed"))}
                        className="rounded-none text-xs font-bold" data-testid="shipment-pack-button">
                        <Box size={13} className="mr-1" /> {t("so.pack")}
                      </Button>
                    </>
                  )}
                  {detail.status === "packed" && (
                    <Button size="sm" onClick={() => doAction("ship", {}, t("so.shipped"))}
                      className="rounded-none text-xs font-bold" data-testid="shipment-ship-button">
                      <Truck size={13} className="mr-1" /> {t("so.ship")}
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
